/**
 * m3 echo Frontend - Tenant Module Assignment Dialog
 *
 * Form for assigning or editing a module assignment for a tenant.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import {
  BillingModule,
  TenantModuleAssignment,
  TenantModuleCreate,
  TenantModuleUpdate,
  billingApi,
} from '../../services/billing';

interface TenantModuleDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  tenantId: number;
  assignment: TenantModuleAssignment | null;
}

const toLocalDate = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

interface OptionalDateFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  emptyText: string;
  clearTooltip: string;
}

/**
 * Optional date field that can really look empty.
 *
 * An empty native date input is not visibly empty in Safari: it shows today's
 * date as a grey placeholder, so a cleared field still looks filled, and
 * editing one segment fills the others with today. While the value is empty
 * and the field is not focused, it is therefore a plain text field showing
 * `emptyText`; it only becomes a date input while focused or filled. A
 * half-typed date also reads back as '', so it stays a date input until it is
 * completed or cleared - otherwise it would vanish and be saved as empty
 * instead of being caught by the browser's validation.
 *
 * Safari can also commit its picker straight to the DOM node without a change
 * event, so the value is synced from the node on blur, and clearing
 * resets the node itself.
 */
const OptionalDateField: React.FC<OptionalDateFieldProps> = ({
  label,
  value,
  onChange,
  emptyText,
  clearTooltip,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const [incomplete, setIncomplete] = useState(false);
  const showDateInput = focused || incomplete || value !== '';

  return (
    <TextField
      label={label}
      type={showDateInput ? 'date' : 'text'}
      value={value}
      placeholder={showDateInput ? undefined : emptyText}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        setFocused(false);
        setIncomplete(e.target.validity.badInput);
        onChange(e.target.value);
      }}
      inputRef={inputRef}
      InputLabelProps={{ shrink: true }}
      InputProps={{
        endAdornment: showDateInput ? (
          <InputAdornment position="end" sx={{ mr: 2 }}>
            <Tooltip title={clearTooltip}>
              <IconButton
                size="small"
                aria-label={`${label.split(' (')[0]} leeren`}
                // Keep the click from blurring the input first: the blur
                // would re-read the old value from the node.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (inputRef.current) {
                    inputRef.current.value = '';
                    inputRef.current.blur();
                  }
                  setFocused(false);
                  setIncomplete(false);
                  onChange('');
                }}
                edge="end"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ) : undefined,
      }}
      fullWidth
    />
  );
};

const TenantModuleDialog: React.FC<TenantModuleDialogProps> = ({
  open,
  onClose,
  onSaved,
  tenantId,
  assignment,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isEdit = !!assignment;

  const [modules, setModules] = useState<BillingModule[]>([]);
  const [selectedModule, setSelectedModule] = useState<BillingModule | null>(null);
  const [selectedModules, setSelectedModules] = useState<BillingModule[]>([]);
  const [activeFrom, setActiveFrom] = useState('');
  const [activeUntil, setActiveUntil] = useState('');
  const [billingFrom, setBillingFrom] = useState('');
  const [billingUntil, setBillingUntil] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [notes, setNotes] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    // Load available modules
    billingApi.getModules().then((res) => {
      setModules(res.data.filter((m) => m.is_active));

      if (assignment) {
        // Find the module for this assignment
        const mod = res.data.find((m) => m.id === assignment.module_id);
        setSelectedModule(mod || null);
      }
    }).catch(() => {});

    if (assignment) {
      setActiveFrom(toLocalDate(assignment.active_from));
      setActiveUntil(toLocalDate(assignment.active_until));
      setBillingFrom(toLocalDate(assignment.billing_from));
      setBillingUntil(toLocalDate(assignment.billing_until));
      setDiscountPercent(String(Number(assignment.discount_percent)));
      setNotes(assignment.notes || '');
    } else {
      setSelectedModule(null);
      setSelectedModules([]);
      setActiveFrom(toLocalDate(new Date().toISOString()));
      setActiveUntil('');
      setBillingFrom(toLocalDate(new Date().toISOString()));
      setBillingUntil('');
      setDiscountPercent('0');
      setNotes('');
    }
    setError(null);
  }, [open, assignment]);

  const discount = Math.min(100, Math.max(0, parseFloat(discountPercent) || 0));

  const effectivePrice = isEdit
    ? selectedModule
      ? Number(selectedModule.base_price) * (1 - discount / 100)
      : 0
    : selectedModules.reduce(
        (sum, m) => sum + Number(m.base_price) * (1 - discount / 100),
        0,
      );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isEdit) {
        const data: TenantModuleUpdate = {
          active_from: activeFrom ? new Date(activeFrom).toISOString() : undefined,
          active_until: activeUntil ? new Date(activeUntil).toISOString() : null,
          billing_from: billingFrom ? new Date(billingFrom).toISOString() : null,
          billing_until: billingUntil ? new Date(billingUntil).toISOString() : null,
          discount_percent: discount,
          notes: notes || undefined,
        };
        await billingApi.updateAssignment(assignment!.id, data);
      } else {
        if (selectedModules.length === 0) {
          setError('Bitte mindestens ein Modul auswählen');
          setLoading(false);
          return;
        }
        for (const mod of selectedModules) {
          const data: TenantModuleCreate = {
            module_id: mod.id,
            active_from: new Date(activeFrom).toISOString(),
            active_until: activeUntil ? new Date(activeUntil).toISOString() : null,
            billing_from: billingFrom ? new Date(billingFrom).toISOString() : null,
            billing_until: billingUntil ? new Date(billingUntil).toISOString() : null,
            discount_percent: discount,
            notes: notes || undefined,
          };
          await billingApi.assignModule(tenantId, data);
        }
      }
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isEdit ? 'Zuweisung bearbeiten' : 'Modul zuweisen'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {isEdit ? (
              <Autocomplete
                value={selectedModule}
                onChange={(_, newValue) => setSelectedModule(newValue)}
                options={modules}
                getOptionLabel={(option) => `${option.display_name} (${option.module_type})`}
                renderInput={(params) => (
                  <TextField {...params} label="Modul" required />
                )}
                disabled
                fullWidth
              />
            ) : (
              <Autocomplete
                multiple
                value={selectedModules}
                onChange={(_, newValue) => setSelectedModules(newValue)}
                options={modules}
                getOptionLabel={(option) => `${option.display_name} (${option.module_type})`}
                renderInput={(params) => (
                  <TextField {...params} label="Module" />
                )}
                fullWidth
              />
            )}

            <TextField
              label="Aktiv von"
              type="date"
              value={activeFrom}
              onChange={(e) => setActiveFrom(e.target.value)}
              onBlur={(e) => setActiveFrom(e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <OptionalDateField
              label="Aktiv bis (leer = unbegrenzt)"
              value={activeUntil}
              onChange={setActiveUntil}
              emptyText="unbegrenzt"
              clearTooltip="Leeren (unbegrenzt)"
            />

            <OptionalDateField
              label="Abrechnung von"
              value={billingFrom}
              onChange={setBillingFrom}
              emptyText="leer"
              clearTooltip="Leeren"
            />

            <OptionalDateField
              label="Abrechnung bis"
              value={billingUntil}
              onChange={setBillingUntil}
              emptyText="leer"
              clearTooltip="Leeren"
            />

            <TextField
              label="Rabatt %"
              type="number"
              value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              inputProps={{ min: 0, max: 100, step: 1 }}
              sx={{ '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }, '& input[type=number]': { MozAppearance: 'textfield' } }}
              fullWidth
            />

            <TextField
              label="Notizen"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />

            {(isEdit ? selectedModule : selectedModules.length > 0) && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Effektiver Preis:
                </Typography>
                <Typography variant="h6">
                  {effectivePrice.toFixed(2)} € / Monat
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {isEdit ? 'Speichern' : 'Zuweisen'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TenantModuleDialog;
